import { PagedListRequestInterface, PagedResponseInterface, PaginatedAPIInterface } from '@vendasta/galaxy/table';
import { AssistantType, FunctionInterface } from '@vendasta/ai-assistants';
import { combineLatest, switchMap, Observable, of, map } from 'rxjs';
import { getNamespace } from '../../../core/services/ai-assistant-utils';
import { createConstraintFilters } from '../../../core/services/constraint-utils';
import { AiAssistantService } from '../../../core/services/ai-assistant.service';

export interface FunctionSortOption {
  field: 'id' | 'description';
  direction: 'asc' | 'desc';
}

export class AiToolsTableDataSource implements PaginatedAPIInterface<FunctionInterface, void, FunctionSortOption> {
  constructor(
    private readonly aiAssistantService: AiAssistantService,
    private readonly assistantType: AssistantType,
    private readonly accountGroupId$: Observable<string>,
    private readonly partnerId$: Observable<string>,
  ) {}

  get(req: PagedListRequestInterface<void, FunctionSortOption>): Observable<PagedResponseInterface<FunctionInterface>> {
    const { cursor, pageSize } = req.pagingOptions ?? {};
    const { text } = req.searchOptions ?? {};

    return combineLatest([this.accountGroupId$, this.partnerId$]).pipe(
      switchMap(([accountGroupId, partnerId]) => {
        const namespace = getNamespace(accountGroupId, partnerId);

        if (!namespace) {
          return of(this.createEmptyResponse());
        }

        const constraintFilters = createConstraintFilters(partnerId, accountGroupId, this.assistantType);

        return this.aiAssistantService
          .listFunctions({
            includeGlobal: true,
            cursor,
            pageSize,
            constraintFilters,
            searchTerm: text,
          })
          .pipe(map(this.mapToPagedResponse));
      }),
    );
  }

  private createEmptyResponse(): PagedResponseInterface<FunctionInterface> {
    return {
      data: [],
      pagingMetadata: {
        nextCursor: '',
        hasMore: false,
      },
    };
  }

  private mapToPagedResponse = (res: {
    functions: FunctionInterface[];
    nextCursor: string;
    hasMore: boolean;
  }): PagedResponseInterface<FunctionInterface> => {
    return {
      data: res.functions ?? [],
      pagingMetadata: {
        nextCursor: res.nextCursor ?? '',
        hasMore: res.hasMore ?? false,
      },
    };
  };
}
